/**
 * BugReportRoot — the floating "Report a bug" button + submission modal.
 *
 * Mount once at the top of the app tree (e.g. inside AuthProvider). Renders
 * nothing if no user is signed in.
 *
 * Opening sources:
 *   - User clicks the FAB → screenshots the page, opens the modal
 *   - Anywhere fires `window.dispatchEvent(new CustomEvent('open-bug-report',
 *     { detail: { prefill: { description?, errorStack?, severity? } } }))`
 *     (used by ErrorBoundary on uncaught React errors)
 *
 * On submit, packages everything (description, screenshot, dossier snapshot,
 * breadcrumbs, console + network errors, request_id, app version) and POSTs
 * to /api/bug-reports. A GitHub Issue is mirrored server-side. The user gets
 * a toast with the BUG-ref so they can reference it.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Bug, X } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { bugReportsAPI, APP_VERSION } from '../api/api';
import { getAll as getBreadcrumbs, clear as clearBreadcrumbs } from '../lib/breadcrumbs';
import {
  getConsoleErrors,
  clearConsoleErrors,
  getConsoleWarnings,
  clearConsoleWarnings,
} from '../lib/consoleBuffer';
import { useToast } from '../hooks/use-toast';

/**
 * Capture a screenshot of the current viewport. Returns a PNG Blob or null
 * if html2canvas isn't available or rendering fails. Imported dynamically
 * so the 60kB library only loads when the user actually opens a report.
 */
async function captureScreenshot() {
  try {
    const { default: html2canvas } = await import('html2canvas');
    const canvas = await html2canvas(document.body, {
      logging: false,
      useCORS: true,
      // Cap resolution to keep payload reasonable for the upload (max ~1.5MB)
      scale: Math.min(1, 1280 / Math.max(window.innerWidth, 1)),
      windowWidth: document.documentElement.clientWidth,
      windowHeight: document.documentElement.clientHeight,
    });
    return await new Promise((resolve) => {
      canvas.toBlob((blob) => resolve(blob), 'image/png', 0.85);
    });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[bug-report] screenshot capture failed:', e?.message || e);
    return null;
  }
}

/**
 * Read the live dossier draft if the user is on the DossierEditor page.
 * DossierEditor publishes the snapshot via window.__skilldeck_dossier_draft
 * (a function returning the current state) so we stay decoupled.
 */
function readDossierSnapshot() {
  try {
    const fn = typeof window !== 'undefined' && window.__skilldeck_dossier_draft;
    if (typeof fn === 'function') return fn();
  } catch { /* noop */ }
  return null;
}

export default function BugReportRoot() {
  const { user, currentOrg, isPlatformAdmin } = useAuth();
  const location = useLocation();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [screenshot, setScreenshot] = useState(null); // Blob | null
  const [errorPrefill, setErrorPrefill] = useState(null); // { description, errorStack, severity }
  // Split description: what happened (required) vs. what was expected (optional).
  const [actualText, setActualText] = useState('');
  const [expectedText, setExpectedText] = useState('');
  const [severity, setSeverity] = useState('medium');
  const [attachScreenshot, setAttachScreenshot] = useState(true);
  const [attachDossier, setAttachDossier] = useState(true);

  // Listen for the global open event (ErrorBoundary uses this)
  useEffect(() => {
    const handler = async (event) => {
      const prefill = event?.detail?.prefill || null;
      setErrorPrefill(prefill);
      if (prefill?.description) setActualText(prefill.description);
      if (prefill?.severity) setSeverity(prefill.severity);
      // Try to capture a screenshot too — best effort, ErrorBoundary fallback
      // may have replaced the tree so screenshot can be empty (OK).
      const shot = await captureScreenshot();
      setScreenshot(shot);
      setOpen(true);
    };
    window.addEventListener('open-bug-report', handler);
    return () => window.removeEventListener('open-bug-report', handler);
  }, []);

  // Reset form when closed
  useEffect(() => {
    if (!open) {
      setActualText('');
      setExpectedText('');
      setSeverity('medium');
      setScreenshot(null);
      setErrorPrefill(null);
      setAttachScreenshot(true);
      setAttachDossier(true);
    }
  }, [open]);

  const handleOpen = useCallback(async () => {
    // Snapshot the page BEFORE opening the modal (else the modal is in the shot)
    const shot = await captureScreenshot();
    setScreenshot(shot);
    setOpen(true);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (submitting) return;
    const actual = actualText.trim();
    const expected = expectedText.trim();
    if (!actual) {
      toast({
        title: 'Description requise',
        description: 'Décris ce qui s’est passé.',
        variant: 'destructive',
      });
      return;
    }
    setSubmitting(true);
    try {
      const breadcrumbs = getBreadcrumbs();
      const networkErrors = breadcrumbs
        .filter((b) => b.type === 'api_error')
        .map((b) => b.payload);
      const consoleErrors = getConsoleErrors();
      const consoleWarnings = getConsoleWarnings();
      const dossierSnapshot = attachDossier ? readDossierSnapshot() : null;
      // Match `/dossier/:id`, `/dossiers/:id/edit`, etc.
      const dossierIdMatch = location.pathname.match(
        /\/dossiers?\/([0-9a-fA-F-]{8,})/,
      );
      const dossierId = dossierIdMatch ? dossierIdMatch[1] : null;

      // Composed description (kept as the single human summary stored on the
      // row + used for the issue title); the structured halves go alongside.
      const userDescription = expected
        ? `${actual}\n\n— Attendu : ${expected}`
        : actual;

      const tz = (() => {
        try {
          return {
            name: Intl.DateTimeFormat().resolvedOptions().timeZone,
            offset_min: new Date().getTimezoneOffset(),
          };
        } catch { return null; }
      })();

      const metadata = {
        user_description: userDescription,
        expected_behavior: expected || null,
        actual_behavior: actual,
        severity,
        app_version: APP_VERSION,
        route: `${location.pathname}${location.search}`,
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
        error_stack: errorPrefill?.errorStack || null,
        dossier_id: dossierId,
        dossier_snapshot: dossierSnapshot,
        console_errors: consoleErrors,
        console_warnings: consoleWarnings,
        network_errors: networkErrors,
        breadcrumbs,
        request_id: null, // backend stamps the receiving request's id; bug-time correlation comes from api_error breadcrumbs
        // Who/where context — speeds up "works for me, not for them" triage.
        user_role: currentOrg?.role || null,
        org_plan: currentOrg?.plan || currentOrg?.plan_tier || null,
        org_status: currentOrg?.status || null,
        is_platform_admin: !!isPlatformAdmin,
        viewport: typeof window !== 'undefined'
          ? {
              w: window.innerWidth,
              h: window.innerHeight,
              dpr: window.devicePixelRatio || 1,
            }
          : null,
        timezone: tz,
        client_time: new Date().toISOString(),
      };

      const result = await bugReportsAPI.create(
        metadata,
        attachScreenshot ? screenshot : null,
      );

      const msg = result?.github_issue_url
        ? `Réf ${result.ref} (issue #${result.github_issue_number})`
        : `Réf ${result?.ref || ''}`;
      toast({
        title: 'Merci, bug envoyé 🐛',
        description: msg,
      });
      // Clear so the next bug starts fresh
      clearBreadcrumbs();
      clearConsoleErrors();
      clearConsoleWarnings();
      setOpen(false);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[bug-report] submit failed', e);
      toast({
        title: "Échec de l'envoi",
        description: e?.response?.data?.detail || e?.message || 'Réessaie dans un instant.',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  }, [
    submitting,
    actualText,
    expectedText,
    severity,
    attachDossier,
    attachScreenshot,
    screenshot,
    errorPrefill,
    location.pathname,
    location.search,
    currentOrg,
    isPlatformAdmin,
    toast,
  ]);

  // Don't show on public pages
  if (!user) return null;

  // Hide on the landing page too — testers only see this once signed in
  const isPublic = ['/', '/login', '/register', '/forgot-password', '/reset-password', '/accept-invite']
    .includes(location.pathname);
  if (isPublic) return null;

  const onDossierRoute = /\/dossiers?\/[0-9a-fA-F-]{8,}/.test(location.pathname);

  return (
    <div data-bug-report-root>
      {/* Floating action button */}
      <button
        type="button"
        onClick={handleOpen}
        aria-label="Signaler un bug"
        title="Signaler un bug"
        className="fixed bottom-5 right-5 z-40 flex items-center gap-2 px-4 py-3 rounded-full bg-violet-600 text-white shadow-lg hover:bg-violet-700 transition-colors"
      >
        <Bug className="w-4 h-4" />
        <span className="text-sm font-medium hidden sm:inline">Signaler</span>
      </button>

      {/* Modal */}
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
          <div className="w-full max-w-lg bg-white rounded-xl shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
              <h2 className="text-base font-semibold text-slate-900 flex items-center gap-2">
                <Bug className="w-4 h-4 text-violet-600" />
                Signaler un bug
              </h2>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="text-slate-400 hover:text-slate-700"
                aria-label="Fermer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="px-5 py-4 space-y-4 max-h-[70vh] overflow-y-auto">
              {errorPrefill?.errorStack && (
                <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  Une erreur a été détectée automatiquement et sera jointe au rapport.
                </div>
              )}

              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">
                  Que s'est-il passé ? <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={actualText}
                  onChange={(e) => setActualText(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 bg-white border border-slate-300 rounded-md text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-500"
                  placeholder="Décris le problème : ce qui s'est passé, le message d'erreur…"
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">
                  Qu'attendais-tu ? <span className="text-slate-400">(optionnel)</span>
                </label>
                <textarea
                  value={expectedText}
                  onChange={(e) => setExpectedText(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 bg-white border border-slate-300 rounded-md text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-500"
                  placeholder="Le comportement attendu, s'il diffère de ce qui s'est passé."
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">
                  Sévérité
                </label>
                <div className="flex gap-2">
                  {['low', 'medium', 'high'].map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setSeverity(s)}
                      className={`flex-1 px-3 py-1.5 rounded-md text-xs font-medium border ${
                        severity === s
                          ? 'border-violet-600 bg-violet-50 text-violet-700'
                          : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      {{ low: 'Mineur', medium: 'Moyen', high: 'Bloquant' }[s]}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <label className="flex items-center gap-2 text-xs text-slate-700">
                  <input
                    type="checkbox"
                    checked={attachScreenshot}
                    onChange={(e) => setAttachScreenshot(e.target.checked)}
                  />
                  Joindre une capture d'écran ({screenshot ? 'prête' : 'indisponible'})
                </label>
                {onDossierRoute && (
                  <label className="flex items-center gap-2 text-xs text-slate-700">
                    <input
                      type="checkbox"
                      checked={attachDossier}
                      onChange={(e) => setAttachDossier(e.target.checked)}
                    />
                    Joindre l'état du dossier en cours
                  </label>
                )}
              </div>

              <details className="text-xs text-slate-500">
                <summary className="cursor-pointer">Contexte joint automatiquement</summary>
                <ul className="mt-2 space-y-1 pl-1">
                  <li>Page : <code>{location.pathname}</code></li>
                  <li>Version : <code>{APP_VERSION}</code></li>
                  <li>Utilisateur : <code>{user?.email || user?.id}</code></li>
                  {currentOrg?.id && (
                    <li>Organisation : <code>{currentOrg.name || currentOrg.id}</code></li>
                  )}
                  <li>Breadcrumbs : {getBreadcrumbs().length} entrées</li>
                  <li>Console : {getConsoleErrors().length} erreurs, {getConsoleWarnings().length} warnings</li>
                </ul>
              </details>
            </div>

            <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-200 bg-slate-50">
              <button
                type="button"
                onClick={() => setOpen(false)}
                disabled={submitting}
                className="px-3 py-1.5 text-sm text-slate-700 hover:text-slate-900"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={submitting || !actualText.trim()}
                className="px-4 py-1.5 rounded-md bg-violet-600 text-white text-sm font-medium hover:bg-violet-700 disabled:opacity-50"
              >
                {submitting ? 'Envoi…' : 'Envoyer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
